import React from "react";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { DraftsOutlined, EmailOutlined } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import { conversationHasUnreadMessage } from "@/src/services/messagingService";
import { Stack, Theme, useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import { mergeSx } from "@/src/utils/mergeSx";
import { conversationTableRowGroupStyle } from "./conversationTableRowGroupStyle";
import AlertLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertLegalbird";
import AlertContentLegalbird from "@/src/components/legalbird/AlertLegalbird/AlertContentLegalbird";
import TableBody from "@mui/material/TableBody";

type ConversationTableRowGroupProps = {
  conversation: Conversation;
  setOpenConversationId: (conversationId: number) => void;
};

export default function ConversationTableRowGroup({ conversation, setOpenConversationId }: ConversationTableRowGroupProps) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const hasUnreadMessage = conversationHasUnreadMessage(conversation);

  const latestMessageCreatedDateDayjs = dayjs(conversation.latestMessageCreatedDate);

  const latestMessageCreatedDateIsToday = latestMessageCreatedDateDayjs.isSame(dayjs(), "day");
  const latestMessageCreatedDateIsThisYear = latestMessageCreatedDateDayjs.isSame(dayjs(), "year");

  let dateStringToDisplay;

  switch (true) {
    case latestMessageCreatedDateIsToday:
      dateStringToDisplay = latestMessageCreatedDateDayjs.format("HH:mm");
      break;
    case latestMessageCreatedDateIsThisYear:
      dateStringToDisplay = latestMessageCreatedDateDayjs.format("DD.MMM");
      break;
    default:
      dateStringToDisplay = latestMessageCreatedDateDayjs.format("DD.MM.YYYY, HH:mm") + " Uhr";
      break;
  }

  const hasNewestMessageHint = conversation.latestMessageCreatedDate !== conversation.created;

  return (
    <TableBody
      onClick={() => conversation.id && setOpenConversationId(conversation.id)}
      sx={{
        cursor: "pointer",
        "&:hover .MuiTableRow-root": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <TableRow hover sx={conversationTableRowGroupStyle.row}>
        <TableCell
          sx={mergeSx(
            { display: { xs: "none", md: "table-cell" } },
            conversationTableRowGroupStyle.column,
            !hasNewestMessageHint && conversationTableRowGroupStyle.columnBorder,
          )}
        >
          <Stack
            sx={{
              justifyContent: "center",
            }}
          >
            {hasUnreadMessage ? (
              <Stack sx={mergeSx(conversationTableRowGroupStyle.unreadIconContainer, conversationTableRowGroupStyle.icon)}>
                <EmailOutlined />
              </Stack>
            ) : (
              <DraftsOutlined sx={conversationTableRowGroupStyle.icon} />
            )}
          </Stack>
        </TableCell>
        <TableCell
          sx={mergeSx(
            { maxWidth: 0, width: "100%" },
            conversationTableRowGroupStyle.column,
            conversationTableRowGroupStyle.columnCenter,
            !hasNewestMessageHint && conversationTableRowGroupStyle.columnBorder,
          )}
        >
          <Typography
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {hasUnreadMessage ? <strong>{conversation.subject}</strong> : conversation.subject}
          </Typography>
        </TableCell>
        <TableCell sx={mergeSx({ width: "auto" }, conversationTableRowGroupStyle.column, !hasNewestMessageHint && conversationTableRowGroupStyle.columnBorder)}>
          <Typography>{dateStringToDisplay}</Typography>
        </TableCell>
      </TableRow>
      {hasNewestMessageHint && (
        <TableRow sx={conversationTableRowGroupStyle.row}>
          <TableCell
            colSpan={isMobile ? 2 : 3}
            sx={mergeSx(conversationTableRowGroupStyle.column, conversationTableRowGroupStyle.columnAlert, conversationTableRowGroupStyle.columnBorder)}
          >
            <AlertLegalbird severity={"warning"} sx={{ width: "fit-content" }}>
              <AlertContentLegalbird title={`Neueste Nachricht: ${dayjs(conversation.latestMessageCreatedDate).format("DD.MM.YYYY, HH:mm")} Uhr`} />
            </AlertLegalbird>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}
